import React from 'react';
import { FileText, MessageCircle } from 'lucide-react';

/**
 * DailyReportViewer — shows narrative + metrics snapshot.
 * Props:
 *   report: GrowthReport (or null)
 *   onGenerate?: () => void
 *   generating?: bool
 */
export default function DailyReportViewer({ report, onGenerate, generating }) {
  if (!report) {
    return (
      <div className="rounded-xl p-6 bg-[#0a0820]/70 border border-gold/25 text-center">
        <FileText className="w-8 h-8 text-gold/60 mx-auto mb-3" />
        <p className="text-text-muted text-sm mb-3">अभी तक कोई report generate नहीं हुई है।</p>
        <button
          onClick={onGenerate}
          disabled={generating}
          data-testid="growth-os-generate-report-btn"
          className="px-4 py-2 rounded-lg bg-gold text-[#0a0820] font-semibold text-sm hover:brightness-110 disabled:opacity-50"
        >
          {generating ? '⏳ बन रही है…' : '📝 अभी Report बनाएँ'}
        </button>
      </div>
    );
  }

  const sent = (report.sent_via || []).length > 0;
  const generatedAt = report.generated_at
    ? new Date(report.generated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
    : '—';

  return (
    <div className="rounded-xl bg-gradient-to-br from-[#1a1230] via-[#0f0b25] to-[#0a0820] border border-gold/40 p-5 md:p-6" data-testid="growth-os-report-viewer">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gold font-semibold">
            {report.type === 'weekly' ? 'साप्ताहिक Report' : 'दैनिक Report'}
          </div>
          <div className="font-heading text-xl text-text-main mt-0.5">
            {report.date_range_from}
            {report.date_range_from !== report.date_range_to && ` → ${report.date_range_to}`}
          </div>
          <div className="text-xs text-text-muted mt-0.5">Generated: {generatedAt} IST</div>
        </div>
        <div className="flex items-center gap-2">
          {sent && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              <MessageCircle className="w-3 h-3" /> WhatsApp Sent
            </span>
          )}
          <button
            onClick={onGenerate}
            disabled={generating}
            data-testid="growth-os-regenerate-btn"
            className="px-3 py-1 rounded-lg border border-gold/50 text-gold text-xs font-semibold hover:bg-gold hover:text-[#0a0820] disabled:opacity-50 transition-colors"
          >
            {generating ? '⏳' : '↻ Regenerate'}
          </button>
        </div>
      </div>

      <div className="prose prose-invert prose-sm max-w-none text-text-main whitespace-pre-wrap font-body leading-relaxed border-t border-gold/15 pt-4">
        {report.narrative_hi}
      </div>
    </div>
  );
}
